import React from "react";
import { Form, Col, Row, Overlay, Popover, Button } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";

import { deepEqual } from "../../utils/object";
import CountrySelect, { exists as CountryValid } from "../select/country";
import StateSelect, { exists as StateValid } from "../select/state";
import CountySelect, { exists as CountyValid } from "../select/county";
import { isGuid } from "../../utils/string";

function normalizeComparable(value) {
  if (value == null) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value).trim();
}

function formatSuggestionValue(value, path = "") {
  if (value == null || value === "") return "[blank]";

  const lowerPath = String(path || "").toLowerCase();

  if (lowerPath.includes("zipcode")) return String(value);
  if (typeof value === "object") return JSON.stringify(value, null, 2);

  return String(value);
}

function normalizeUsZipCode(value, { preserveTrailingDash = false } = {}) {
  const text = String(value ?? "");
  const digits = text.replace(/\D/g, "").slice(0, 9);
  if (preserveTrailingDash && /-\s*$/.test(text) && digits.length === 5) return `${digits}-`;
  if (digits.length > 5) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  return digits;
}

function sanitizeUsZipInput(value) {
  const text = String(value ?? "").trim();
  if (/^-\d{5,9}$/.test(text)) return normalizeUsZipCode(text);
  return text.replace(/[^\d-]/g, "").slice(0, 10);
}

class Address extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      ...this.propsToState(props),
      uuid: isGuid(props.uuid) ? props.uuid : uuidv4(),
      suggestionMenu: {
        show: false,
        target: null,
        path: null,
      },
    };
  }

  propsToState = (props = {}) => ({
    simple:
      typeof props.simple === "boolean"
        ? props.simple
        : String(props.simple ?? "").trim().toLowerCase() === "true",

    line1: String(props.line1 ?? ""),
    line2: String(props.line2 ?? ""),
    city: String(props.city ?? ""),
    state: String(props.state ?? ""),
    county: String(props.county ?? ""),
    zipCode: sanitizeUsZipInput(props.zipCode),
    country: String(props.country ?? "US").trim().toUpperCase(),
  });

  componentDidUpdate(prevProps) {
    const oldSlice = this.propsToState(prevProps);
    const newSlice = this.propsToState(this.props);

    const incomingUuid = isGuid(this.props.uuid) ? this.props.uuid : this.state.uuid;
    const uuidChanged = incomingUuid !== this.state.uuid;
    const propsChanged = !deepEqual(oldSlice, newSlice);

    if (!propsChanged && !uuidChanged) return;

    this.setState((prev) => ({
      ...prev,
      ...newSlice,
      uuid: incomingUuid,
    }));
  }
  
  setAndNotify = (patch) => {
    this.setState(
      (prev) => ({ ...prev, ...patch }),
      () => this.props.onChange?.(this.state)
    );
  };

  onChange = (e) => {
    this.setAndNotify({ [e.target.name]: e.target.value });
  };

  onUsZipCodeChange = (event) => {
    this.setAndNotify({ zipCode: event.target.value.replace(/[^\d-]/g, "").slice(0, 10) });
  };

  onUsZipCodeBlur = () => {
    this.setAndNotify({ zipCode: normalizeUsZipCode(this.state.zipCode) });
  };

  onCountryChange = (opt) => {
    const v = opt?.value ?? "";
    if (!CountryValid(v)) return;

    this.setAndNotify({
      country: v,
      ...(v !== "US" ? { county: "", state: "" } : {}),
    });
  };

  onStateChange = (opt) => {
    const v = opt ?? "";
    if (!StateValid(v)) return;
    this.setAndNotify({ state: v, county: "" });
  };

  onCountyChange = (opt) => {
    const v = opt ?? "";
    if (!v) {
      this.setAndNotify({ county: "" });
      return;
    }
    if (CountyValid(this.state.state, v)) {
      this.setAndNotify({ county: v });
    }
  };

  copyToClipboard = async (content) => {
    try {
      await navigator.clipboard.writeText(content ?? "");
    } catch {}
  };

  hasSuggestion = (path) => {
    const incoming = this.props.suggestions?.[path];
    if (incoming === undefined) return false;

    const current = this.state?.[path];
    return normalizeComparable(current) !== normalizeComparable(incoming);
  };

  openSuggestionMenu = (event, path) => {
    event.preventDefault();
    event.stopPropagation();

    this.setState({
      suggestionMenu: {
        show: true,
        target: event.currentTarget,
        path,
      },
    });
  };

  closeSuggestionMenu = () => {
    this.setState((prev) => ({
      suggestionMenu: {
        ...prev.suggestionMenu,
        show: false,
        target: null,
        path: null,
      },
    }));
  };

  applySuggestion = () => {
    const path = this.state?.suggestionMenu?.path;
    if (!path) return;

    const value = this.props.suggestions?.[path];
    if (this.props.onApplySuggestion) this.props.onApplySuggestion(path, value);
    this.closeSuggestionMenu();
  };

  rejectSuggestion = () => {
    const path = this.state?.suggestionMenu?.path;
    if (!path) return;

    const value = this.props.suggestions?.[path];
    if (this.props.onRejectSuggestion) this.props.onRejectSuggestion(path, value);
    this.closeSuggestionMenu();
  };

  renderLabel = (text, path, copyValue = null, style = undefined) => {
    const showIcon = this.hasSuggestion(path);

    return (
      <Form.Label
        style={{ ...(style || {}), ...(copyValue !== null ? { cursor: "copy" } : {}) }}
        onClick={copyValue !== null ? () => this.copyToClipboard(copyValue) : undefined}
      >
        {text}
        {showIcon ? (
          <button
            type="button"
            onClick={(e) => this.openSuggestionMenu(e, path)}
            style={{
              border: 0,
              background: "transparent",
              padding: 0,
              marginLeft: 6,
              lineHeight: 1,
              fontSize: "0.95rem",
              cursor: "pointer",
            }}
            title="Review imported survey value"
          >
            ✨
          </button>
        ) : null}
      </Form.Label>
    );
  };

  render() {
    const __simple = this.state.simple;
    const activeSuggestionPath = this.state?.suggestionMenu?.path;
    const activeSuggestionValue = activeSuggestionPath
      ? this.props.suggestions?.[activeSuggestionPath]
      : undefined;

    return (
      <>
        {!__simple ? (
          <>
            <Row className={this.props.rowClassName}>
              <Col lg="12">
                <div className="mb-3 top-label">
                  <Form.Control value={this.state.line1} type="text" name="line1" onChange={this.onChange} />
                  {this.renderLabel("ADDRESS (Line 1)", "line1", this.state.line1)}
                </div>
              </Col>
            </Row>

            <Row className={this.props.rowClassName}>
              <Col lg="12">
                <div className="mb-3 top-label">
                  <Form.Control value={this.state.line2} type="text" name="line2" onChange={this.onChange} />
                  {this.renderLabel("ADDRESS (Line 2)", "line2", this.state.line2)}
                </div>
              </Col>
            </Row>
          </>
        ) : null}

        <Row className={this.props.rowClassName}>
          <Col lg="12">
            <div className="mb-3 top-label">
              <CountrySelect value={this.state.country || "US"} onChange={this.onCountryChange} placeholder="" name="country" />
              {this.renderLabel("COUNTRY", "country")}
            </div>
          </Col>
        </Row>

        {this.state.country === "US" ? (
          <Row className={this.props.rowClassName}>
            <Col lg={__simple ? 6 : 4}>
              <div className="mb-3 top-label">
                <Form.Control value={this.state.city} type="text" name="city" onChange={this.onChange} />
                {this.renderLabel("CITY", "city", this.state.city)}
              </div>
            </Col>

            <Col lg="3">
              <div className="mb-3 top-label">
                <StateSelect value={this.state.state} onChange={this.onStateChange} placeholder="" name="state" />
                {this.renderLabel("STATE", "state")}
              </div>
            </Col>

            <Col lg="3">
              <div className="mb-3 top-label">
                <CountySelect value={this.state.county} state={this.state.state} onChange={this.onCountyChange} placeholder="" name="county" />
                {this.renderLabel("COUNTY", "county")}
              </div>
            </Col>

            {!__simple ? (
              <Col lg="2">
                <div className="mb-3 top-label">
                  <Form.Control
                    value={this.state.zipCode}
                    type="text"
                    name="zipCode"
                    inputMode="text"
                    autoComplete="postal-code"
                    maxLength={10}
                    onChange={this.onUsZipCodeChange}
                    onBlur={this.onUsZipCodeBlur}
                  />
                  {this.renderLabel("ZIP CODE", "zipCode")}
                </div>
              </Col>
            ) : null}
          </Row>
        ) : (
          <Row className={this.props.rowClassName}>
            <Col lg={__simple ? 6 : 4}>
              <div className="mb-3 top-label">
                <Form.Control value={this.state.city} type="text" name="city" onChange={this.onChange} />
                {this.renderLabel("CITY", "city", this.state.city)}
              </div>
            </Col>

            <Col lg={__simple ? 6 : 4}>
              <div className="mb-3 top-label">
                <Form.Control value={this.state.state} type="text" name="state" onChange={this.onChange} />
                {this.renderLabel("STATE or PROVINCE", "state")}
              </div>
            </Col>

            {!__simple ? (
              <Col lg={4}>
                <div className="mb-3 top-label">
                  <Form.Control value={this.state.zipCode} type="text" name="zipCode" onChange={this.onChange} />
                  {this.renderLabel("ZIP or POSTAL CODE", "zipCode")}
                </div>
              </Col>
            ) : null}
          </Row>
        )}

        <Overlay
          target={this.state?.suggestionMenu?.target}
          show={this.state?.suggestionMenu?.show}
          placement="bottom-end"
          rootClose
          onHide={this.closeSuggestionMenu}
        >
          {(overlayProps) => (
            <Popover {...overlayProps}>
              <Popover.Header as="h3">Imported survey value</Popover.Header>
              <Popover.Body>
                <div className="small text-muted mb-2">{activeSuggestionPath}</div>
                <div className="mb-3" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {formatSuggestionValue(activeSuggestionValue, activeSuggestionPath)}
                </div>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="primary" onClick={this.applySuggestion}>
                    Accept
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={this.rejectSuggestion}>
                    Reject
                  </Button>
                </div>
              </Popover.Body>
            </Popover>
          )}
        </Overlay>
      </>
    );
  }
}

Address.defaultProps = {
  suggestions: {},
  onApplySuggestion: null,
  onRejectSuggestion: null,
};

export default Address;
